import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Card,
  CardBody,
  Table,
  Badge,
  Alert,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Label,
  CardHeader,
  ModalFooter,
  Progress,
} from "reactstrap";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { SettingsIcon } from "lucide-react";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  getClientProjects,
  getClientProjectDetails,
  lockTask,
  lockSubTask,
  updateTaskMaxHours,
  updateSubTaskMaxHours,
  lockChildSubTask,
  updateChildSubTaskMaxHours,
  updateTaskDueDate,
  updateSubTaskDueDate,
} from "../../Api/ApiUrlConst";

import { ProjectDTO, SubTaskDTO } from "../../types/client.types";
import ClientInputModal from "../ClientInput/ClientInputModal";
import { useAuth } from "../../Context/AuthContext";
import Comments from "../CommentComponent/Comments";

/* ================= TYPES ================= */

export interface TaskDTO {
  id: number;
  description: string;
  assignedToName?: string;
  createdByName?: string;
  dueDate: string;
  status: string;
  priority?: string;
  percentCompleted: number;
  completionDate?: string;

  isDueDateLocked: boolean;
  isClientInputEnabled: boolean;

  maxHours?: number;     // ✅ ADD
  spentHours?: number;

  subTasks?: SubTaskDTO[];
}

interface ChildSubTask {
  spentHours?: number;
  maxHours?: number;
}

interface SubTask {
  childSubTasks?: ChildSubTask[];
}

interface Task {
  subTasks?: SubTask[];
}


/* ================= COMPONENT ================= */

const ClientProjectsWithTasks: React.FC = () => {
  const { userID, department } = useAuth();
  const [projects, setProjects] = useState<ProjectDTO[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [tasks, setTasks] = useState<TaskDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showSubtasks, setShowSubtasks] = useState<Record<number, boolean>>({});
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);


  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");


  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{
    type: "task" | "subtask";
    id: number;
  } | null>(null);

  const [editingTaskDueId, setEditingTaskDueId] = useState<number | null>(null);
  const [editingSubTaskDueId, setEditingSubTaskDueId] = useState<number | null>(null);
  const [editingChildDueId, setEditingChildDueId] = useState<number | null>(null);


  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingSubTaskId, setEditingSubTaskId] = useState<number | null>(null);
  const [tempMaxHours, setTempMaxHours] = useState<number>(0);

  const [showChildSubtasks, setShowChildSubtasks] = useState<Record<number, boolean>>({});
  const [editingChildId, setEditingChildId] = useState<number | null>(null);
  const [dueEditValue, setDueEditValue] = useState<string>("");

const [openComments, setOpenComments] = useState<Record<number, boolean>>({});


  const statusColorMap: Record<string, string> = {
    'Yet to Start': 'status-yet-to-start',
    YetToStart: 'status-yet-to-start',

    'On Hold': 'status-on-hold',
    OnHold: 'status-on-hold',

    'Waiting for Input': 'status-waiting-input',
    WaitingForInput: 'status-waiting-input',

    'Modelling in Progress': 'status-modelling-progress',
    ModellingInProgress: 'status-modelling-progress',

    'Modelling ToDo': 'status-modelling-Todo',
    ModellingTodo: 'status-modelling-Todo',

    'Modelling Completed': 'status-modelling-completed',
    ModellingCompleted: 'status-modelling-completed',

    'Drafting in Progress': 'status-drafting-progress',
    DraftingInProgress: 'status-drafting-progress',

    'Drafting ToDo': 'status-drafting-Todo',
    DraftingTodo: 'status-drafting-Todo',

    'Drafting Completed': 'status-drafting-completed',
    DraftingCompleted: 'status-drafting-completed',

    'QC ToDo': 'status-qc-Todo',
    QCTodo: 'status-qc-Todo',

    'QC in Progress': 'status-qc-progress',
    QCInProgress: 'status-qc-progress',

    'QC Completed': 'status-qc-completed',
    QCCompleted: 'status-qc-completed',

    'Client Review in Progress': 'status-client-review-progress',
    ClientReviewInProgress: 'status-client-review-progress',

    'Client Review ToDo': 'status-client-review-Todo',
    ClientReviewTodo: 'status-client-review-Todo',



    'Client Review Completed': 'status-client-review-completed',
    ClientReviewCompleted: 'status-client-review-completed',

    'Revision in Progress': 'status-Revision-progress',
    RevisionInProgress: 'status-Revision-progress',

    'Revision ToDo': 'status-Revision-Todo',
    RevisionTodo: 'status-Revision-Todo',

    'Revision Completed': 'status-Revision-completed',
    RevisionCompleted: 'status-Revision-completed',


    'Final Correction in Progress': 'status-final-correction-progress',
    FinalCorrectionInProgress: 'status-final-correction-progress',

    'Final Correction ToDo': 'status-final-correction-Todo',
    FinalCorrectionTodo: 'status-final-correction-Todo',

    'Final Correction Completed': 'status-final-correction-completed',
    FinalCorrectionCompleted: 'status-final-correction-completed',

    'Not In Scope': 'status-Not-In-Scope',
    NotInScope: 'status-Not-In-Scope',

    Released: 'status-released',
    'Completed & Closed': 'status-completed',
    Completed: 'status-completed',

    Cancelled: 'status-cancelled',
    Issues: 'status-issues'
  };


  const priorityColorMap: Record<string, string> = {
    Low: 'priority-low',
    Medium: 'priority-medium',
    High: 'priority-high',
    Critical: 'priority-critical',
  };


  const allStatuses = [
    "YetToStart",
    "WaitingForInput",
    "Cancelled",
    "OnHold",
    "Issuse",
    "Released",
    "ModellingTodo",
    "DraftingTodo",
    "QCTodo",
    "RevisionTodo",
    "ClientReviewTodo",
    "FinalCorrectionTodo",
    "ModellingInProgress",
    "DraftingInProgress",
    "QCInProgress",
    "RevisionInProgress",
    "ClientReviewInProgress",
    "FinalCorrectionInProgress",
    "ModellingCompleted",
    "DraftingCompleted",
    "QCCompleted",
    "RevisionCompleted",
    "ClientReviewCompleted",
    "FinalCorrectionCompleted",
    // "Completed",
  ];


  const inProgressStatuses = [
    "ModellingInProgress",
    "DraftingInProgress",
    "QCInProgress",
    "RevisionInProgress",
    "ClientReviewInProgress",
    "FinalCorrectionInProgress"
  ];

  const completedStatuses = [
    "ModellingCompleted",
    "DraftingCompleted",
    "QCCompleted",
    "RevisionCompleted",
    "ClientReviewCompleted",
    "FinalCorrectionCompleted",
  ];

  const ToDoStatuses = [
    "ModellingTodo",
    "DraftingTodo",
    "QCTodo",
    "RevisionTodo",
    "ClientReviewTodo",
    "FinalCorrectionTodo"
  ];


  const getChildTotals = (children: ChildSubTask[] = []) => {
    return children.reduce(
      (acc, c) => {
        acc.spent += Number(c.spentHours || 0);
        acc.max += Number(c.maxHours || 0);
        return acc;
      },
      { spent: 0, max: 0 }
    );
  };

  const getSubTaskTotals = (subTasks: SubTask[] = []) => {
    return subTasks.reduce(
      (acc, st) => {
        const childTotals = getChildTotals(st.childSubTasks || []);
        acc.spent += childTotals.spent;
        acc.max += childTotals.max;
        return acc;
      },
      { spent: 0, max: 0 }
    );
  };

  /* ================= LOAD PROJECTS ================= */

  useEffect(() => {
    (async () => {
      try {
        const res = await getClientProjects();
        setProjects(res.data);
      } catch {
        setError("Failed to load projects");
      }
    })();
  }, []);

  /* ================= LOAD TASKS ================= */

  const loadProjectTasks = async (projectId: number) => {
    setSelectedProjectId(projectId);
    setLoading(true);
    setError(null);

    try {
      const res = await getClientProjectDetails(projectId);

      // 🔥 Keep old expand states
      const oldShowSubtasks = { ...showSubtasks };
      const oldShowChildSubtasks = { ...showChildSubtasks };

      setTasks(res.data.tasks || []);

      // 🔥 Restore expand state
      setShowSubtasks(oldShowSubtasks);
      setShowChildSubtasks(oldShowChildSubtasks);

    } catch {
      setError("Failed to load project tasks");
    } finally {
      setLoading(false);
    }
  };
  /* ================= HELPERS ================= */

  const countWeekdaysSigned = (start: Date, end: Date) => {
    const a = new Date(start);
    const b = new Date(end);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);

    if (a.getTime() === b.getTime()) return 0;

    const forward = b > a;
    let d = new Date(a);
    let count = 0;

    // move away from start date (exclude start)
    d.setDate(d.getDate() + (forward ? 1 : -1));

    while (forward ? d <= b : d >= b) {
      const day = d.getDay(); // 0=Sun, 6=Sat
      if (day !== 0 && day !== 6) {
        count += forward ? 1 : -1; // 🔥 sign handled here
      }
      d.setDate(d.getDate() + (forward ? 1 : -1));
    }

    return count;
  };

  const DEFAULT_DUE_COLORS = {
    greaterThan30: "#C8C8C8",   // rgb(200,200,200)
    lessThan30: "#646464",      // rgb(100,100,100)
    lessThan10: "#000000",
    lessThan5: "#0061ff",
    overdue: "#f9001a",
    onTime: "#669c35",
    completed: "#ffffff",
  };


  const [dueColorSettings, setDueColorSettings] = useState(() => {
    const saved = localStorage.getItem("dueColorSettings");
    return saved ? JSON.parse(saved) : DEFAULT_DUE_COLORS;
  });


  const getDueColor = (diff: number, isCompleted: boolean) => {
    if (isCompleted) return dueColorSettings.completed;

    if (diff < 0) return dueColorSettings.overdue;
    if (diff <= 5) return dueColorSettings.lessThan5;
    if (diff <= 10) return dueColorSettings.lessThan10;
    if (diff < 30) return dueColorSettings.lessThan30;

    return dueColorSettings.greaterThan30; // ✅ fixed
  };
  const renderCompletionCell = (task: TaskDTO) => {

    // ✅ Get biggest subtask due date first
    const maxSubDate = getMaxSubtaskDate(task.subTasks ?? []);

    const dueDateString = maxSubDate || task.dueDate;

    if (!dueDateString) return <td>—</td>;

    const due = new Date(dueDateString);
    const today = new Date();
    const completed = task.completionDate
      ? new Date(task.completionDate)
      : null;

    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    if (completed) completed.setHours(0, 0, 0, 0);

    const isCompleted =
      task.status?.toLowerCase() === "released" && completed;

    const calculateDiff = (start: Date, end: Date) =>
      useWeekdays
        ? countWeekdaysSigned(start, end)
        : Math.round(
          (end.getTime() - start.getTime()) /
          (1000 * 60 * 60 * 24)
        );

    let diff = 0;
    let tooltipText = "";
    let displayText = "";

    if (isCompleted) {
      diff = calculateDiff(due, completed!);

      if (diff === 0) {
        tooltipText = "Completed On Time";
      } else if (diff < 0) {
        tooltipText = `Completed Early by ${Math.abs(diff)} days`;
      } else {
        tooltipText = `Completed Late by ${diff} days`;
      }

      displayText = "Completed";
    } else {
      // ✅ IMPORTANT: today first, due second
      diff = calculateDiff(today, due);

      if (diff === 0) {
        displayText = "0 day";
        tooltipText = "Due today";
      } else if (diff < 0) {
        displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
        tooltipText = `Overdue by ${Math.abs(diff)} days`;
      } else {
        displayText = `${diff} day${diff === 1 ? "" : "s"}`;
        tooltipText = `${diff} days remaining`;
      }
    }

    const color = getDueColor(diff, !!isCompleted);

    return (
      <td
        style={{ color, fontWeight: 600 }}
        title={tooltipText}
      >
        {displayText}
      </td>
    );
  };





  // ---------------- LOCK HANDLERS ----------------
  const toggleTaskLock = async (taskId: number, locked: boolean) => {
    await lockTask(taskId, locked);
    if (selectedProjectId) loadProjectTasks(selectedProjectId);
  };

  const toggleSubTaskLock = async (subTaskId: number, locked: boolean) => {
    await lockSubTask(subTaskId, locked);
    if (selectedProjectId) loadProjectTasks(selectedProjectId);
  };

  // ---------------- HELPERS ----------------
  const formatDate = (date?: string | null) => {
    if (!date) return "—";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };



  const toggleSubtaskView = (taskId: number) => {
    setShowSubtasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // ===================================================
  // ✅ FINAL FILTER PIPELINE (TYPE SAFE)
  // ===================================================
  const mechanicalUsers: string[] = [
    "Umesh Prasad",
    "Santhosh R",
    "Venkatesh SG",
    "Santhosh S",
    "Aravind M",
    "Vishnu Prasath",
    "Sabari Raghavan",
    "Richard Joy",
    "Dinesh V",
    "Naveen E"
  ];

  const electricalUsers: string[] = [
    "Hari Prasath",
    "Renganathan",
    "Limya Shilin",
    "Vishal",
    "Renganathan P",
    "RavindraBabu",
    "Limya",
    "Rizalde Barrato"
  ];


  // 1️⃣ Start from tasks state (single source of truth)
  let filteredTasks = tasks.filter(task => {

    if (selectedDepartment === "All") return true;

    if (selectedDepartment === "Mechanical")
      return mechanicalUsers.includes(task.assignedToName ?? "");

    if (selectedDepartment === "Electrical")
      return electricalUsers.includes(task.assignedToName ?? "");

    return true;
  });


  // 3️⃣ Status MODAL multi-select filter (Task OR Subtask)
  if (selectedStatuses.length > 0) {
    filteredTasks = filteredTasks.reduce<TaskDTO[]>((acc, task) => {
      const taskMatches = selectedStatuses.includes(task.status);

      const matchingSubtasks = (task.subTasks || []).filter(
        (st: SubTaskDTO) => selectedStatuses.includes(st.status)
      );

      if (taskMatches || matchingSubtasks.length > 0) {
        acc.push({
          ...task,
          subTasks: taskMatches ? task.subTasks : matchingSubtasks,
        });
      }

      return acc;
    }, []);
  }


  // column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "percent",
    "spendhours",
    "maxHours",
  ]);


  // completed / released toggle
  const [showCompletedReleased, setShowCompletedReleased] = useState<boolean>(() => {
    const saved = localStorage.getItem("showCompletedReleased");
    return saved ? saved === "true" : true;
  });

  // weekdays vs all days
  const [useWeekdays, setUseWeekdays] = useState<boolean>(() => {
    const saved = localStorage.getItem("useWeekdays");
    return saved ? saved === "false" : true;
  });

  const [showNotInScope, setshowNotInScope] = useState<boolean>(() => {
    const saved = localStorage.getItem("showNotInScope");
    return saved ? saved === "false" : false;
  });

  //   const [showNotInScope, setshowNotInScope] = useState(
  //   localStorage.getItem("showNotInScope") === "true"
  // );


  filteredTasks = filteredTasks.reduce<TaskDTO[]>((acc, task) => {
    const isCompletedOrReleased =
      task.status === "Completed" ||
      task.status === "Released";

    const isNotInScope = task.status === "NotInScope";

    // hide completed/released tasks
    if (!showCompletedReleased && isCompletedOrReleased) {
      return acc;
    }

    // hide NotInScope tasks
    if (!showNotInScope && isNotInScope) {
      return acc;
    }

    const activeSubTasks = (task.subTasks || []).filter(st => {
      if (!showCompletedReleased &&
        (st.status === "Completed" || st.status === "Released"))
        return false;

      if (!showNotInScope && st.status === "NotInScope")
        return false;

      return true;
    });

    acc.push({
      ...task,
      subTasks: activeSubTasks,
    });

    return acc;
  }, []);


  const toggleChildSubtaskView = (subTaskId: number) => {
    setShowChildSubtasks(prev => ({
      ...prev,
      [subTaskId]: !prev[subTaskId],
    }));
  };

  const toggleChildSubTaskLock = async (childId: number, locked: boolean) => {
    await lockChildSubTask(childId, locked);
    if (selectedProjectId) loadProjectTasks(selectedProjectId);
  };




  const selectedProject = projects.find(p => p.id === selectedProjectId);


  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };


  const UserAvatar = ({ name }: { name: string }) => {
    const initials = getInitials(name);

    return (
      <div
        title={name}
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#008BFF",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 600,
          marginRight: 4,
          cursor: "default",
        }}
      >
        {initials}
      </div>
    );
  };

  /* ================= RENDER ================= */

  const toDateInputValue = (date?: string | null) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };


  const getMaxSubtaskDate = (subTasks: any[]) => {
    if (!subTasks || subTasks.length === 0) return null;

    const validDates = subTasks
      .map(s => s.dueDate ? new Date(s.dueDate) : null)
      .filter((d): d is Date => d !== null);

    if (validDates.length === 0) return null;

    const maxDate = new Date(
      Math.max(...validDates.map(d => d.getTime()))
    );

    return maxDate.toISOString();
  };

  const handleSave = async () => {
    if (!editingSubTaskDueId) return;

    const value = dueEditValue || null;

    await updateSubTaskDueDate(editingSubTaskDueId, value);

    setEditingSubTaskDueId(null);
    setDueEditValue("");

    if (selectedProjectId) loadProjectTasks(selectedProjectId);
  };



  return (
    <Container fluid>
      {/* ---------- HEADER ---------- */}
      <Row className="mb-2 p-2 bg-light rounded">
        <Col md={4}>
          <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
            <DropdownToggle caret color="white" className="fw-bold">
              {selectedProject
                ? `${selectedProject.projectID} - ${selectedProject.projectName}`
                : "Select Project"}
            </DropdownToggle>
            <DropdownMenu>
              {projects.map(p => (
                <DropdownItem key={p.id} onClick={() => loadProjectTasks(p.id)}>
                  {p.projectID} - {p.projectName}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </Col>

        {selectedProject && (<>
          <Col >
            <Input
              type="select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="mt-2 fw-bold"
              style={{
                width: '140px'
              }}
            >
              <option value="All">All</option>
              <option value="Mechanical">Mechanical</option>
              <option value="Electrical">Electrical</option>
            </Input>
          </Col>
          <Col lg={6} md={12} sm={12} className="project-details">
            <div className="d-flex flex-wrap align-items-center gap-2">

              <Button color="light" onClick={() => setStatusModalOpen(true)}>
                Status Filter
              </Button>

              <Button
                color="light"
                onClick={() => {
                  setSelectedStatuses([]);
                  if (selectedProjectId) loadProjectTasks(selectedProjectId);
                }}
              >
                Clear
              </Button>

              <Button color="light" onClick={() => setSettingsOpen(true)}>
                <SettingsIcon size={18} />
              </Button>

              {/* <div className="d-flex align-items-center gap-1 fw-bold ms-auto">
                <span>Project Schedule:</span>
                <span>{formatDate(selectedProject.startDate)}</span>
                <span>-</span>
                <span>{formatDate(selectedProject.endDate)}</span>
              </div> */}

            </div>
          </Col>
        </>
        )}


      </Row>
      {statusModalOpen && (
        <Card>
          <CardHeader>
            {/* <div className="fw-bold mb-2">Filter by Status</div> */}

            <Row className="row-cols-1 row-cols-md-4 g-2 fw-semibold align-items-center">

              <Col className="d-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  checked={selectedStatuses.length === allStatuses.length}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedStatuses(allStatuses);
                    else setSelectedStatuses([]);
                  }}
                />
                <span>Select All</span>
              </Col>
              <Col className="d-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  checked={inProgressStatuses.every(s => selectedStatuses.includes(s))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...inProgressStatuses])));
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => !inProgressStatuses.includes(s)));
                    }
                  }}
                />
                <span>Select All In-Progress</span>
              </Col>
              <Col className="d-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  checked={ToDoStatuses.every(s => selectedStatuses.includes(s))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...ToDoStatuses])));
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => !ToDoStatuses.includes(s)));
                    }
                  }}
                />
                <span>Select All ToDo</span>
              </Col>

              <Col className="d-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  checked={completedStatuses.every(s => selectedStatuses.includes(s))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...completedStatuses])));
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => !completedStatuses.includes(s)));
                    }
                  }}
                />
                <span>Select All Completed</span>
                <Button style={{ marginLeft: "200px" }} close onClick={() => setStatusModalOpen(false)} />
              </Col>


            </Row>
          </CardHeader>


          <CardBody style={{ padding: "20px" }}>

            {/* --------- Master Controls --------- */}


            {/* --------- Status List --------- */}
            <Row className="row-cols-6">
              {allStatuses.map((status) => (
                <Col key={status} className="mb-2">
                  <div className="d-flex align-items-center">
                    <Input
                      id={`status-${status}`}
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => {
                        selectedStatuses.includes(status)
                          ? setSelectedStatuses(selectedStatuses.filter(s => s !== status))
                          : setSelectedStatuses([...selectedStatuses, status]);
                      }}
                    />
                    <Label check htmlFor={`status-${status}`} className="ms-2" style={{ cursor: "pointer" }}>
                      {status}
                    </Label>
                  </div>
                </Col>
              ))}
            </Row>



          </CardBody>
        </Card>
      )}


      {/* ---------- TABLE ---------- */}
      <Card>
        <CardBody>
          {!selectedProject && <Alert color="info">Please select a project</Alert>}
          {loading && <Spinner />}
          {error && <Alert color="danger">{error}</Alert>}

          {selectedProject && !loading && (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  {visibleColumns.includes("maxHours") && <th>Max.Hrs</th>}
                  <th>Due Date</th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={(e) => {
                      e.stopPropagation(); // prevent sort if any
                      setUseWeekdays(prev => {
                        const newValue = !prev;
                        localStorage.setItem("useWeekdays", String(newValue));
                        return newValue;
                      });
                    }}
                    title={
                      useWeekdays
                        ? "Click to switch to Calendar Days (Mon–Sun)"
                        : "Click to switch to Working Days (Mon–Fri)"
                    }
                  >
                    Due {useWeekdays ? "(WD)" : "(CD)"}
                  </th>
                  {visibleColumns.includes("spendhours") && <th>Time</th>}
                  {visibleColumns.includes("percent") && <th>Work Done</th>}
                  {/* <th>Created By</th> */}
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th>Priority</th>


                  <th className="d-flex align-items-center">Input <InfoOutlineIcon /></th>


                </tr>
              </thead>

              <tbody>
                {filteredTasks.map((task: TaskDTO) => {

                  const taskTotals = getSubTaskTotals(task.subTasks || []);

                  return (




                    <React.Fragment key={task.id}>

                      {/* TASK ROW */}
                      <tr>
                        <td>
                          {(task.subTasks?.length ?? 0) > 0 && (
                            <Button
                              size="sm"
                              color="link"
                              onClick={() => toggleSubtaskView(task.id)}
                            >
                              {showSubtasks[task.id] ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </Button>
                          )}
                        </td>


                        <td>{task.description}</td>

                        {visibleColumns.includes("maxHours") && (
                          <td>{taskTotals.max} H</td>
                        )}

                        <td>
                          {editingTaskDueId === task.id ? (
                            <Input
                              type="date"
                              autoFocus
                              defaultValue={toDateInputValue(task.dueDate)}
                              onBlur={async (e) => {
                                const value = e.target.value || null;

                                await updateTaskDueDate(task.id, value);

                                setEditingTaskDueId(null);

                                if (selectedProjectId)
                                  loadProjectTasks(selectedProjectId);
                              }}
                              onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                  const value =
                                    (e.target as HTMLInputElement).value || null;

                                  await updateTaskDueDate(task.id, value);

                                  setEditingTaskDueId(null);

                                  if (selectedProjectId)
                                    loadProjectTasks(selectedProjectId);
                                }

                                if (e.key === "Escape") {
                                  setEditingTaskDueId(null);
                                }
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                cursor:
                                  task.isDueDateLocked ||
                                    (task.subTasks && task.subTasks.length > 0)
                                    ? "not-allowed"
                                    : "pointer"
                              }}
                              title={
                                task.isDueDateLocked
                                  ? "Due date is locked"
                                  : task.subTasks && task.subTasks.length > 0
                                    ? "Edit subtask due dates"
                                    : "Double click to edit"
                              }
                              onDoubleClick={() => {
                                if (
                                  !task.isDueDateLocked &&
                                  (!task.subTasks || task.subTasks.length === 0)
                                ) {
                                  setEditingTaskDueId(task.id);
                                }
                              }}
                            >
                              {(() => {
                                const maxSubDate = getMaxSubtaskDate(task.subTasks ?? []);

                                const displayDate = maxSubDate || task.dueDate;
                                return displayDate ? formatDate(displayDate) : "—";
                              })()}
                            </div>
                          )}
                        </td>



                        <td> {renderCompletionCell(task)}</td>

                        {visibleColumns.includes("spendhours") && (
                          <td style={{ minWidth: "90px" }}>
                            <div
                              title={`Spent: ${taskTotals.spent} H
Remaining: ${Math.max(
                                taskTotals.max - taskTotals.spent,
                                0
                              )} H`}
                            >
                              <Progress
                                value={
                                  taskTotals.max > 0
                                    ? (taskTotals.spent / taskTotals.max) * 100
                                    : 0
                                }
                              // color={
                              //   taskTotals.spent > taskTotals.max
                              //     ? "danger"
                              //     : taskTotals.spent > taskTotals.max * 0.8
                              //     ? "warning"
                              //     : "success"
                              // }
                              >
                                {/* {taskTotals.spent} / {taskTotals.max} H */}
                              </Progress>
                            </div>
                          </td>
                        )}



                        {visibleColumns.includes("percent") && (
                          <td data-label="Percent" style={{ minWidth: "100px" }}>
                            <div className="d-flex flex-column gap-1">
                              <Progress
                                value={task.percentCompleted ?? 0}
                                max={100}
                                title={` ${task.percentCompleted ?? 0}% Completed`}
                              >
                              </Progress>
                            </div>
                          </td>
                        )}



                        {/* <td>{task.createdByName}</td> */}
                        <td>{task.assignedToName}</td>
                        <td>
                          <Badge className={statusColorMap[task.status]}>
                            {task.status}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            className={priorityColorMap[task.priority ?? "Medium"] ?? "bg-info"}>
                            {task.priority}
                          </Badge>
                        </td>



                        {/* 🔥 CLIENT INPUT */}
                        <td>
                          {task.isClientInputEnabled && (
                            <Button
                              size="sm"
                              color="link"
                              onClick={() => {
                                setSelectedEntity({ type: "task", id: task.id });
                                setInputModalOpen(true);
                              }}
                            >
                              <HelpOutlineIcon />
                            </Button>
                          )}
                        </td>




                      </tr>

                      {/* SUBTASKS */}
                      {showSubtasks[task.id] &&
                        task.subTasks?.map(st => {

                          const childTotals = getChildTotals(st.childSubTasks || []);

                          return (
                            <React.Fragment key={st.id}>
                              {/* -------- SUBTASK ROW -------- */}
                              <tr>
                                <td>
                                  {(st.childSubTasks?.length ?? 0) > 0 && (
                                    <Button
                                      size="sm"
                                      color="link"
                                      style={{ marginLeft: "30px" }}
                                      onClick={() => {
  toggleChildSubtaskView(st.id);
  setOpenComments(prev => ({ ...prev, [st.id]: !prev[st.id] }));
}}
                                    >
                                      {showChildSubtasks[st.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </Button>
                                  )}
                                </td>

                                <td className="ps-4"> {st.description}</td>



                                {visibleColumns.includes("maxHours") && (
                                  <td>{childTotals.max} H</td>
                                )}
                                <td>
                                  {editingSubTaskDueId === st.id ? (
                                    <Input
                                      type="date"
                                      autoFocus
                                      value={dueEditValue || toDateInputValue(st.dueDate)}
                                      onChange={(e) => setDueEditValue(e.target.value)}
                                      onBlur={handleSave}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSave();
                                        if (e.key === "Escape") {
                                          setEditingSubTaskDueId(null);
                                          setDueEditValue("");
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        cursor: st.isDueDateLocked ? "not-allowed" : "pointer"
                                      }}
                                      title={
                                        st.isDueDateLocked
                                          ? "Due date is locked"
                                          : "Double click to edit"
                                      }
                                      onDoubleClick={() => {
                                        if (!st.isDueDateLocked) {
                                          setDueEditValue(toDateInputValue(st.dueDate));
                                          setEditingSubTaskDueId(st.id);
                                        }
                                      }}
                                    >
                                      {formatDate(st.dueDate)}
                                    </div>
                                  )}
                                </td>


                                <td>{renderCompletionCell(st)}</td>

                                {visibleColumns.includes("spendhours") && (
                                  <td style={{ minWidth: "80px" }}>
                                    <div
                                      title={`Spent: ${childTotals.spent} H
Remaining: ${Math.max(
                                        childTotals.max - childTotals.spent,
                                        0
                                      )} H`}
                                    >
                                      <Progress
                                        value={
                                          childTotals.max > 0
                                            ? (childTotals.spent / childTotals.max) * 100
                                            : 0
                                        }

                                      // color={
                                      //   childTotals.spent > childTotals.max
                                      //     ? "danger"
                                      //     : childTotals.spent > childTotals.max * 0.8
                                      //     ? "warning"
                                      //     : "success"
                                      // }
                                      >
                                        {/* {childTotals.spent} / {childTotals.max} H */}
                                      </Progress>
                                    </div>
                                  </td>
                                )}





                                {visibleColumns.includes("percent") && (
                                  <td data-label="Percent" style={{ minWidth: "100px" }}>
                                    <div className="d-flex flex-column gap-1">
                                      <Progress
                                        value={st.percentCompleted ?? 0}
                                        max={100}
                                        title={` ${st.percentCompleted ?? 0}% Completed`}
                                      >
                                      </Progress>
                                    </div>
                                  </td>
                                )}

                                {/* <td>{st.createdByName}</td> */}
                                <td>{st.assignedToName}</td>

                                <td>
                                  <Badge className={statusColorMap[st.status]}>
                                    {st.status}
                                  </Badge>
                                </td>

                                <td>
                                  <Badge className={priorityColorMap[st.priority ?? "Medium"]}>
                                    {st.priority}
                                  </Badge>
                                </td>



                                <td>
                                  {st.isClientInputEnabled && (
                                    <Button
                                      size="sm"
                                      color="link"
                                      onClick={() => {
                                        setSelectedEntity({ type: "subtask", id: st.id });
                                        setInputModalOpen(true);
                                      }}
                                    >
                                      <HelpOutlineIcon />
                                    </Button>
                                  )}
                                </td>
                              </tr>

                              {/* -------- CHILD SUBTASKS -------- */}
                              {showChildSubtasks[st.id] &&
                                st.childSubTasks?.map(child => (<>
                                  <tr key={child.id} style={{ background: "#eef6ff" }}>
                                    <td />

                                    <td className="ps-5">↳ {child.description}</td>

                                    {visibleColumns.includes("maxHours") && (
                                      <td>
                                        {editingChildId === child.id ? (
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            autoFocus
                                            style={{ width: "80px" }}
                                            defaultValue={child.maxHours ?? 0}
                                            onBlur={async (e) => {
                                              const value = Number(e.target.value) || 0;

                                              await updateChildSubTaskMaxHours(child.id, value);

                                              // ✅ Update locally instead of reload
                                              setTasks(prev =>
                                                prev.map(task => ({
                                                  ...task,
                                                  subTasks: task.subTasks?.map(st => ({
                                                    ...st,
                                                    childSubTasks: st.childSubTasks?.map(cs =>
                                                      cs.id === child.id
                                                        ? { ...cs, maxHours: value }
                                                        : cs
                                                    )
                                                  }))
                                                }))
                                              );

                                              setEditingChildId(null);
                                            }}
                                            onKeyDown={async (e) => {
                                              if (e.key === "Enter") {
                                                const value =
                                                  Number((e.target as HTMLInputElement).value) || 0;

                                                await updateChildSubTaskMaxHours(child.id, value);

                                                setTasks(prev =>
                                                  prev.map(task => ({
                                                    ...task,
                                                    subTasks: task.subTasks?.map(st => ({
                                                      ...st,
                                                      childSubTasks: st.childSubTasks?.map(cs =>
                                                        cs.id === child.id
                                                          ? { ...cs, maxHours: value }
                                                          : cs
                                                      )
                                                    }))
                                                  }))
                                                );

                                                setEditingChildId(null);
                                              }

                                              if (e.key === "Escape") {
                                                setEditingChildId(null);
                                              }
                                            }}
                                          />
                                        ) : (
                                          <div
                                            style={{ cursor: "pointer" }}
                                            onDoubleClick={() => setEditingChildId(child.id)}
                                            title="Double click to edit"
                                          >
                                            <b>{child.maxHours ?? 0} H</b>
                                          </div>
                                        )}
                                      </td>
                                    )}

                                    <td>
                                      {/* {editingChildDueId === child.id ? (
    <Input
      type="date"
      autoFocus
      defaultValue={toDateInputValue(child.dueDate)}
      onBlur={async (e) => {
        const value = e.target.value;

        await updateChildSubTaskDueDate(child.id, value);

        // ✅ update locally
        setTasks(prev =>
          prev.map(task => ({
            ...task,
            subTasks: task.subTasks?.map(st => ({
              ...st,
              childSubTasks: st.childSubTasks?.map(cs =>
                cs.id === child.id
                  ? { ...cs, dueDate: value }
                  : cs
              )
            }))
          }))
        );

        setEditingChildDueId(null);
      }}
      onKeyDown={async (e) => {
        if (e.key === "Enter") {
          const value = (e.target as HTMLInputElement).value;

          await updateChildSubTaskDueDate(child.id, value);

          setTasks(prev =>
            prev.map(task => ({
              ...task,
              subTasks: task.subTasks?.map(st => ({
                ...st,
                childSubTasks: st.childSubTasks?.map(cs =>
                  cs.id === child.id
                    ? { ...cs, dueDate: value }
                    : cs
                )
              }))
            }))
          );

          setEditingChildDueId(null);
        }

        if (e.key === "Escape") setEditingChildDueId(null);
      }}
    />
  ) : (
    <div
      style={{
        cursor: child.isDueDateLocked ? "not-allowed" : "pointer"
      }}
      title={
        child.isDueDateLocked
          ? "Due date is locked"
          : "Double click to edit"
      }
      onDoubleClick={() => {
        if (!child.isDueDateLocked)
          setEditingChildDueId(child.id);
      }}
    > */}
                                      {formatDate(child.dueDate)}
                                      {/* </div>
  )} */}
                                    </td>



                                    <td>{renderCompletionCell(child)}</td>


                                    {visibleColumns.includes("spendhours") && (
                                      <td style={{ minWidth: "80px" }}>
                                        <div
                                          title={`Spent: ${child.spentHours ?? 0} H
Remaining: ${Math.max(
                                            (child.maxHours ?? 0) - (child.spentHours ?? 0),
                                            0
                                          )} H`}
                                        >
                                          <Progress
                                            value={
                                              (child.maxHours ?? 0) > 0
                                                ? ((child.spentHours ?? 0) / (child.maxHours ?? 1)) * 100
                                                : 0
                                            }
                                          >
                                          </Progress>
                                        </div>
                                      </td>
                                    )}
                                    {visibleColumns.includes("percent") && (
                                      <td data-label="Percent" style={{ minWidth: "100px" }}>
                                        <div className="d-flex flex-column gap-1">
                                          <Progress
                                            value={child.percentCompleted ?? 0}
                                            max={100}
                                            title={` ${child.percentCompleted ?? 0}% Completed`}
                                          >
                                          </Progress>
                                        </div>
                                      </td>
                                    )}

                                    {/* <td>{st.createdByName}</td> */}

                                    <td>
                                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        {(child.assignedTeamMemberNames || []).map((name) => (
                                          <UserAvatar key={name} name={name} />
                                        ))}
                                      </div>
                                    </td>


                                    <td>
                                      <Badge className={statusColorMap[child.status]}>
                                        {child.status}
                                      </Badge>
                                    </td>

                                    <td>
                                      <Badge className={priorityColorMap[child.priority ?? "Medium"]}>
                                        {child.priority}
                                      </Badge>
                                    </td>



                                    <td />
                                  </tr>

                                </>
                                
                                ))}
                             {openComments[st.id] && (
  <tr>
    <td
      colSpan={visibleColumns.length}
      style={{
        background: "#f4f5f7",
        padding: "16px 24px",
        borderTop: "1px solid #dfe1e6",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          border: "1px solid #dfe1e6",
          padding: "16px",
          marginLeft: "40px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Comments
          isOpen={true}
          onClose={() => {}}
          entityType="subtask"
          entityId={st.id}
          title={st.description}
          inlineMode={true}
        />
      </div>
    </td>
  </tr>
)}
                            </React.Fragment>
                          );

                        })}



                    </React.Fragment>
                  );

                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
      <Modal isOpen={settingsOpen} toggle={() => setSettingsOpen(false)} centered>
        <ModalHeader toggle={() => setSettingsOpen(false)}>Settings</ModalHeader>
        <ModalBody>

          {/* Toggle Percent Column */}
          <div className="d-flex mb-2">
            <Label>
              <Input
                type="checkbox"
                checked={visibleColumns.includes("percent")}
                onChange={() => {
                  setVisibleColumns(prev => {
                    const updated = prev.includes("percent")
                      ? prev.filter(c => c !== "percent")
                      : [...prev, "percent"];

                    localStorage.setItem("visibleColumns", JSON.stringify(updated)); // 🔥 Save permanently
                    return updated;
                  });
                }}
              />
              <span className="ms-2">Show Work Done</span>
            </Label>
          </div>
          <div className="d-flex mb-2">
            <Label>
              <Input
                type="checkbox"
                checked={
                  visibleColumns.includes("spendhours") &&
                  visibleColumns.includes("maxHours")
                }
                onChange={() => {
                  setVisibleColumns(prev => {
                    const hasAll =
                      prev.includes("spendhours") &&
                      prev.includes("maxHours");

                    return hasAll
                      ? prev.filter(
                        c => !["spendhours", "maxHours"].includes(c)
                      )
                      : [
                        ...prev,
                        ...["spendhours", "maxHours"].filter(
                          c => !prev.includes(c)
                        ),
                      ];
                  });
                }}
              />
              <span className="ms-2">Show Spent / Max</span>
            </Label>
          </div>


          <div className="d-flex mb-2">
            <Label>
              <Input
                type="checkbox"
                checked={showCompletedReleased}
                onChange={() => {
                  setShowCompletedReleased(prev => {
                    const newValue = !prev;
                    localStorage.setItem("showCompletedReleased", String(newValue)); // 🔥 Save permanently
                    return newValue;
                  });
                }}
              />
              <span className="ms-2">Show Completed & Released</span>
            </Label>
          </div>
          <div className="d-flex mb-2">
            <Label>
              <Input
                type="checkbox"
                checked={showNotInScope}
                onChange={() => {
                  setshowNotInScope(prev => {
                    const newValue = !prev;
                    localStorage.setItem("showNotInScope", String(newValue));
                    return newValue;
                  });
                }}
              />

              <span className="ms-2">Show Not In Scope</span>
            </Label>
          </div>



          <hr />
          <h6>Due Color Settings</h6>

          {Object.keys(dueColorSettings).map(key => (<>
            <div key={key} className="d-flex align-items-center mb-2">
              <Label style={{ width: "160px" }}>{key}</Label>
              <Input
                type="color"
                value={dueColorSettings[key]}
                onChange={(e) => {
                  const updated = {
                    ...dueColorSettings,
                    [key]: e.target.value,
                  };
                  setDueColorSettings(updated);
                  localStorage.setItem("dueColorSettings", JSON.stringify(updated));
                }}
                style={{ width: "60px", height: "35px", padding: 2 }}
              />


            </div>
          </>
          ))}

          <div className="mt-3">
            <Button
              color="secondary"
              size="sm"
              onClick={() => {
                setDueColorSettings(DEFAULT_DUE_COLORS);
                localStorage.setItem(
                  "dueColorSettings",
                  JSON.stringify(DEFAULT_DUE_COLORS)
                );
              }}
            >
              Reset to Default
            </Button>
          </div>



        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => {
            // Save to localStorage permanently
            localStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
            localStorage.setItem("showCompletedReleased", String(showCompletedReleased));
            localStorage.setItem("useWeekdays", String(useWeekdays));

            setSettingsOpen(false); // close modal
          }}>
            Apply
          </Button>
        </ModalFooter>

      </Modal>
      {/* ---------- CLIENT INPUT MODAL ---------- */}
      {inputModalOpen && selectedEntity && userID && (
        <ClientInputModal
          isOpen={inputModalOpen}
          toggle={() => {
            setInputModalOpen(false);
            setSelectedEntity(null);

            // 🔥 VERY IMPORTANT
            if (selectedProjectId) {
              loadProjectTasks(selectedProjectId);
            }
          }}
          entityType={selectedEntity.type}
          entityId={selectedEntity.id}
          isClientInputEnabled={
            selectedEntity.type === "task"
              ? tasks.find(t => t.id === selectedEntity.id)
                ?.isClientInputEnabled ?? false
              : tasks
                .flatMap(t => t.subTasks || [])
                .find(st => st.id === selectedEntity.id)
                ?.isClientInputEnabled ?? false
          }
          canCreate={false}
        />
      )}
    </Container>
  );
};

export default ClientProjectsWithTasks;